import { NavLink } from 'react-router-dom'
import { useState } from 'react'
import LanguageSwitcher from './LanguageSwitcher'
import { useTranslation } from '../i18n/translations'
import { useLanguage } from '../contexts/LanguageContext'
import { useDarkMode } from '../App'
import { useAuth } from '../hooks/useAuth'

export default function HeaderDark() {
  const { t } = useTranslation()
  const { isRTL } = useLanguage()
  const { isDark, toggleDark } = useDarkMode()
  const { isAuthenticated, isAdmin, user } = useAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  return (
    <header className={`border-b transition-colors overflow-x-hidden ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} ${isRTL ? 'font-mixed' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 w-full overflow-x-hidden">
        <div className={`flex justify-between items-center h-16 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center ${isRTL ? 'space-x-8 space-x-reverse' : 'space-x-6'}`}>
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-md flex items-center justify-center">
              <svg className={`w-5 h-5 ${isDark ? 'text-gray-900' : 'text-white'}`} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <NavLink to="/" className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              SudanLibrary
            </NavLink>
          </div>
          
          <nav className={`hidden md:flex items-center ${isRTL ? 'gap-8' : 'space-x-8'}`}>
            <NavLink
              to="/"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? (isDark ? 'text-emerald-400' : 'text-emerald-600')
                    : (isDark ? 'text-gray-300 hover:text-white' : 'text-gray-800 hover:text-gray-900')
                }`
              }
            >
              {t('nav.home')}
            </NavLink>
            <NavLink
              to="/search"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? (isDark ? 'text-emerald-400' : 'text-emerald-600')
                    : (isDark ? 'text-gray-300 hover:text-white' : 'text-gray-800 hover:text-gray-900')
                }`
              }
            >
              {t('nav.search')}
            </NavLink>
            <NavLink
              to="/about"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? (isDark ? 'text-emerald-400' : 'text-emerald-600')
                    : (isDark ? 'text-gray-300 hover:text-white' : 'text-gray-800 hover:text-gray-900')
                }`
              }
            >
              {t('nav.about')}
            </NavLink>
          </nav>

          <div className={`flex items-center space-x-2 sm:space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 transition-colors rounded-md ${
                isDark 
                  ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              title="Menu"
            >
              {isMobileMenuOpen ? (
                // Close icon
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                // Hamburger icon
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>

            <LanguageSwitcher />
            
            {/* Dark mode toggle */}
            <button 
              onClick={toggleDark}
              className={`p-2 transition-colors rounded-md ${
                isDark 
                  ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              title={isDark ? t('theme.lightMode') : t('theme.darkMode')}
            >
              {isDark ? (
                // Sun icon for light mode
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                // Moon icon for dark mode
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            
            {/* Only show admin button if user is already authenticated and has admin role */}
            {isAdmin && (
              <NavLink
                to="/admin"
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  isDark
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                }`}
              >
                {t('nav.admin')}
              </NavLink>
            )}
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className={`md:hidden border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="px-2 pt-2 pb-3 space-y-1">
              <NavLink
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActive
                      ? (isDark ? 'bg-gray-800 text-emerald-400' : 'bg-gray-100 text-emerald-600')
                      : (isDark ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-800 hover:text-gray-900 hover:bg-gray-100')
                  }`
                }
              >
                {t('nav.home')}
              </NavLink>
              <NavLink
                to="/search"
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActive
                      ? (isDark ? 'bg-gray-800 text-emerald-400' : 'bg-gray-100 text-emerald-600')
                      : (isDark ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-800 hover:text-gray-900 hover:bg-gray-100')
                  }`
                }
              >
                {t('nav.search')}
              </NavLink>
              <NavLink
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActive
                      ? (isDark ? 'bg-gray-800 text-emerald-400' : 'bg-gray-100 text-emerald-600')
                      : (isDark ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-800 hover:text-gray-900 hover:bg-gray-100')
                  }`
                }
              >
                {t('nav.about')}
              </NavLink>
              {isAdmin && (
                <NavLink
                  to="/admin"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isDark
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                >
                  {t('nav.admin')}
                </NavLink>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}